"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getProductImageUrl } from "@/lib/utils";

export interface ProductCardData {
  id: number;
  title: string;
  slug: string;
  price_ht: number | null;
  display_rent_price_ht: number | null;
  selling_type: string | null;
  store_name: string | null;
  image_local: string | null;
  quote_only: boolean;
}

interface ProductsSectionProps {
  locationProducts: ProductCardData[];
  achatProducts: ProductCardData[];
}

const PRODUCTS_PER_PAGE = 5;

export function ProductsSection({ locationProducts, achatProducts }: ProductsSectionProps) {
  const [activeTab, setActiveTab] = useState<"location" | "achat">("location");
  const [page, setPage] = useState(0);
  const mobileScrollRef = useRef<HTMLDivElement>(null);

  const products = activeTab === "location" ? locationProducts : achatProducts;
  const totalPages = Math.ceil(products.length / PRODUCTS_PER_PAGE);
  const firstVisibleIndex = page * PRODUCTS_PER_PAGE;
  const lastVisibleIndex = firstVisibleIndex + PRODUCTS_PER_PAGE;

  const handleTabChange = (tab: "location" | "achat") => {
    setActiveTab(tab);
    setPage(0);
    mobileScrollRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const handlePrev = () => setPage((p) => (p > 0 ? p - 1 : totalPages - 1));
  const handleNext = () => setPage((p) => (p < totalPages - 1 ? p + 1 : 0));

  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Image src="/logo-vignette.png" alt="" width={32} height={32} className="h-8 w-8" />
            Nos produits les plus populaires
          </h2>

          <div className="flex items-center gap-3">
            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => handleTabChange("location")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === "location"
                    ? "bg-secondary text-white"
                    : "bg-white text-secondary border-2 border-secondary hover:bg-secondary/10"
                }`}
              >
                Location
              </button>
              <button
                onClick={() => handleTabChange("achat")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === "achat"
                    ? "bg-secondary text-white"
                    : "bg-white text-secondary border-2 border-secondary hover:bg-secondary/10"
                }`}
              >
                Achat
              </button>
            </div>

            {/* Navigation arrows */}
            {totalPages > 1 && (
              <div className="hidden md:flex gap-1">
                <button
                  onClick={handlePrev}
                  className="h-10 w-10 rounded-full border border-secondary flex items-center justify-center hover:bg-secondary/10 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-secondary" />
                </button>
                <button
                  onClick={handleNext}
                  className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/90 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div
          ref={mobileScrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 pb-2 md:mx-0 md:px-0 md:pb-0 md:grid md:grid-cols-3 lg:grid-cols-5 md:overflow-visible"
        >
          {products.map((product, index) => {
            const price = product.display_rent_price_ht ?? product.price_ht;
            const isRent = product.selling_type === "RENT";
            const isVisibleOnDesktop = index >= firstVisibleIndex && index < lastVisibleIndex;

            return (
              <Link
                key={product.id}
                href={`/produits/${product.slug}`}
                className={`bg-white rounded-xl border hover:shadow-lg transition-shadow group overflow-hidden flex flex-col flex-none w-[78vw] max-w-[300px] snap-start md:w-auto md:max-w-none md:min-w-0 ${
                  isVisibleOnDesktop ? "md:flex" : "md:hidden"
                }`}
              >
                <div className="aspect-square relative bg-white p-4">
                  <Image
                    src={getProductImageUrl(product.image_local)}
                    alt={product.title}
                    fill
                    className="object-contain p-2"
                  />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-medium text-sm mb-1 line-clamp-2 group-hover:text-secondary transition-colors min-h-[2.5rem]">
                    {product.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    {product.store_name || "TOOLOUER"}
                  </p>
                  {!product.quote_only && price != null && Number(price) > 0 ? (
                    <div className="mb-3 mt-auto">
                      {isRent && (
                        <p className="text-[11px] text-muted-foreground leading-none mb-0.5">À partir de</p>
                      )}
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold whitespace-nowrap">
                          {Number(price).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </span>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          HT{isRent ? " / jour" : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3 mt-auto">
                      <span className="text-sm font-medium text-secondary">Sur devis</span>
                    </div>
                  )}
                  <span className="block w-full text-center bg-secondary hover:bg-secondary/90 text-white text-sm font-medium py-2 rounded-md">
                    {product.quote_only || price == null || Number(price) <= 0
                      ? "Demander un devis"
                      : isRent ? "Louer ce produit" : "Acheter ce produit"}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
